// @/components/reusable/listbox.jsx
// V2 Listbox — selection control with spring-animated dropdown
// Sizes: sm (compact, like split-button) | md (standard, like v2 dropdown)
// Matches v2/reusable/dropdown animation, hover, and blur patterns.

import React, { createContext, useContext } from "react";
import * as Headless from "@headlessui/react";
import { motion } from "motion/react";
import clsx from "clsx";

import { dropdownSpring, quickExit } from "./animations";

// ============================================================================
// SIZE SYSTEM
// ============================================================================

const sizeConfig = {
  sm: {
    button: "min-h-8 py-1 pr-7 pl-3 text-xs/5 rounded-lg",
    focusRing: "after:rounded-lg",
    options: "rounded-lg p-0.5",
    option: "rounded-lg py-1.5 pl-3 pr-1.5 text-xs/5 grid-cols-[1fr_--spacing(4)]",
    check: "size-3.5",
    chevron: "size-3.5",
    bg: "bg-white",
  },
  md: {
    button: "min-h-10 py-2 pr-10 pl-3.5 text-sm/6 rounded-xl font-bold",
    focusRing: "after:rounded-xl",
    options: "rounded-xl p-1.5",
    option: "rounded-xl py-2 pl-3.5 pr-2 text-sm/6 grid-cols-[1fr_--spacing(5)]",
    check: "size-4",
    chevron: "size-4",
    bg: "bg-white/95 backdrop-blur-xl",
  },
};

// ============================================================================
// VARIANT SYSTEM (button trigger only — dropdown is always light)
// ============================================================================

const variantConfig = {
  outline: {
    text: "text-black",
    bg: "bg-zinc-50/50 shadow-xs",
    ring: "ring-1 ring-zinc-200 group-data-active:ring-zinc-300 group-data-hover:ring-zinc-300",
    hover: "group-data-hover:shadow-none",
    disabled: "group-data-disabled:ring-zinc-200/60 group-data-disabled:shadow-none",
    chevron: "stroke-zinc-400 group-data-hover:stroke-zinc-600",
    placeholder: "text-zinc-500",
  },
  solid: {
    text: "text-zinc-100",
    bg: "bg-black",
    ring: "",
    hover: "group-data-hover:bg-zinc-800",
    disabled: "group-data-disabled:opacity-50",
    chevron: "stroke-zinc-400 group-data-hover:stroke-zinc-300",
    placeholder: "text-zinc-500",
  },
};

// Pass size down to children without prop drilling
const SizeCtx = createContext("md");

// ============================================================================
// LISTBOX (root)
// ============================================================================

/**
 * Listbox — controlled single-select with spring-animated dropdown
 *
 * @param {"sm"|"md"} [size="md"]       – Compact or standard sizing
 * @param {"outline-solid"|"solid"} [variant="outline"] – Button trigger style
 */
export function Listbox({
  className,
  placeholder,
  autoFocus,
  "aria-label": ariaLabel,
  children: options,
  size = "md",
  variant = "",
  ...props
}) {
  const s = sizeConfig[size] || sizeConfig.md;
  const v = variantConfig[variant] || variantConfig.outline;

  return (
    <SizeCtx.Provider value={size}>
      <Headless.Listbox {...props} multiple={false}>
        <Headless.ListboxButton
          autoFocus={autoFocus}
          data-slot="control"
          aria-label={ariaLabel}
          className={clsx(
            className,
            "group relative block w-full cursor-pointer",
            // Focus ring — sits on an ::after pseudo so it doesn't shift layout
            "focus:outline-hidden",
            "after:pointer-events-none after:absolute after:inset-0 after:ring-inset after:ring-transparent",
            s.focusRing,
            "data-focus:after:ring-2 data-focus:after:ring-primary",
            // Disabled
            "data-disabled:opacity-50",
          )}
        >
          <Headless.ListboxSelectedOption
            as="span"
            options={options}
            placeholder={
              placeholder && (
                <span className={clsx("block truncate", v.placeholder)}>
                  {placeholder}
                </span>
              )
            }
            className={clsx(
              // Layout
              "relative block w-full appearance-none",
              s.button,
              // Typography
              "text-left font-fono font-medium tracking-tighter",
              v.text,
              // Ring / border
              v.ring,
              // Background
              v.bg,
              // Hover
              "transition-all duration-150",
              v.hover,
              // Disabled
              v.disabled,
            )}
          />
          {/* Chevron */}
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
            <svg
              className={clsx(
                s.chevron,
                v.chevron,
                "transition-colors duration-150",
              )}
              viewBox="0 0 16 16"
              aria-hidden="true"
              fill="none"
            >
              <path
                d="M5.75 10.75L8 13L10.25 10.75"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.25 5.25L8 3L5.75 5.25"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </Headless.ListboxButton>

        {/* Options panel — spring animation matching v2/dropdown */}
        <Headless.ListboxOptions
          as={motion.div}
          initial={{ opacity: 0, scale: 0.96, y: -4 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            transition: dropdownSpring,
          }}
          exit={{
            opacity: 0,
            scale: 0.96,
            y: -4,
            transition: quickExit,
          }}
          portal
          anchor="bottom start"
          className={clsx(
            // Anchor gap
            "[--anchor-gap:--spacing(2)]",
            // Base
            "isolate z-50 w-max min-w-(--button-width)",
            "select-none scroll-py-1",
            s.options,
            // Outline (invisible, for forced-colors a11y)
            "outline-solid outline-1 outline-transparent focus:outline-hidden",
            // Scrolling
            "overflow-y-auto overscroll-contain max-h-40!",
            // Background — md gets blur, sm stays solid
            s.bg,
            // Shadow
            "shadow-lg ring-1 ring-black/5",
          )}
        >
          {options}
        </Headless.ListboxOptions>
      </Headless.Listbox>
    </SizeCtx.Provider>
  );
}

// ============================================================================
// LISTBOX OPTION
// ============================================================================

export function ListboxOption({ children, className, ...props }) {
  const size = useContext(SizeCtx);
  const s = sizeConfig[size] || sizeConfig.md;

  const baseClasses = clsx(
    "flex min-w-0 items-center",
    // Icon slots — size only (color handled per context)
    "*:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0",
  );

  return (
    <Headless.ListboxOption as={React.Fragment} {...props}>
      {({ selectedOption }) => {
        // Inside the button trigger — icons inherit button text color
        if (selectedOption) {
          return (
            <div className={clsx(className, baseClasses)}>{children}</div>
          );
        }

        // Inside the dropdown panel — full row with checkmark
        return (
          <div
            className={clsx(
              // Layout
              "group/option grid cursor-pointer items-center gap-x-2.5",
              s.option,
              // Typography
              "text-black",
              // Hover / focus
              "outline-hidden transition-colors duration-100",
              "data-focus:bg-zinc-100 data-focus:text-black",
              // Disabled
              "data-disabled:opacity-50",
            )}
          >
            <span className={clsx(
              className, baseClasses, "col-start-1",
              // Icon colors — only in the dropdown, not in the button trigger
              "*:data-[slot=icon]:text-zinc-500 group-data-focus/option:*:data-[slot=icon]:text-zinc-800",
            )}>
              {children}
            </span>
            {/* Checkmark — right-aligned, visible only for the selected option */}
            <svg
              className={clsx(
                "relative hidden self-center stroke-current group-data-selected/option:inline",
                s.check,
              )}
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      }}
    </Headless.ListboxOption>
  );
}

// ============================================================================
// LISTBOX LABEL & DESCRIPTION
// ============================================================================

export function ListboxLabel({ className, ...props }) {
  return (
    <span
      {...props}
      className={clsx(className, "ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0")}
    />
  );
}

export function ListboxDescription({ className, children, ...props }) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        "flex flex-1 overflow-hidden text-black",
        "before:w-2 before:min-w-0 before:shrink",
        "group-data-focus/option:text-zinc-600",
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
